import { isProductIllustrationPath } from '@trashlab/api/src/domains/pricing/shared/product-illustration';
import env from '@trashlab/sitesapp/src/env';
import {
  Image,
  ImageProps,
} from '@trashlab/sitesapp/src/organizations/fasttrac/components/shared/image';
import { useEffect, useState } from 'react';

interface StorageImageProps extends Omit<ImageProps, 'src'> {
  logoPath: string;
  bucket: string;
  alt: string;
  fit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
}

const StorageImage = ({
  logoPath,
  bucket,
  alt,
  ...props
}: StorageImageProps) => {
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Helper function to check if a string is a valid URL
    const isValidUrl = (string: string): boolean => {
      try {
        new URL(string);
        return true;
      } catch {
        return false;
      }
    };

    function getImageUrl(path: string): string | undefined {
      // If the path is already a public URL, use it directly
      if (isProductIllustrationPath(path) || isValidUrl(path)) {
        return path;
      }

      // In local environment, use an unbranded placeholder logo
      if (env.NEXT_PUBLIC_TRASHLAB_ENV === 'local') {
        return env.NEXT_PUBLIC_APP_HOST + '/site-logo.svg';
      }

      // For non-URL paths, log warning and return undefined
      // All images should be migrated to S3 URLs
      console.warn(
        `StorageImage received non-URL path: ${path}. All images should be migrated to S3 URLs.`,
      );
      return undefined;
    }

    if (logoPath) {
      setLogoUrl(getImageUrl(logoPath));
    } else {
      setLogoUrl(undefined);
    }
  }, [logoPath, bucket]);

  if (!logoUrl) return null;
  if (!isProductIllustrationPath(logoUrl)) {
    return <Image src={logoUrl} alt={alt || 'logo'} {...props} />;
  }
  return (
    <figure className="m-0">
      <Image
        src={logoUrl}
        alt="Representative equipment illustration"
        {...props}
      />
      <figcaption className="mt-1 text-xs text-gray-500">
        Illustration only. Actual equipment may vary.
      </figcaption>
    </figure>
  );
};

export default StorageImage;
